import type {BoardData} from '@github-ui/board-core'

import {type BoardInitialPayload, type BoardPayloadItem, toBoardData as toBoardDataShape} from '../issues/board-payload'

/** TItem is the payload item, carried through the board untouched. */
export type BoardItem = BoardPayloadItem

/**
 * Everything the host needs to mount one board: the `data` contract the board consumes and
 * the axis field identifiers that drive grouping. No cursors and no load-more — this layer
 * renders a settled payload. Paging arrives in a later stack layer (github/views#139).
 */
export type BoardSource = {
  /** The board's contract — the only part `@github-ui/board` ever sees. */
  data: BoardData<BoardItem>
  /** Registry identifier the columns are grouped by. `null` drives D29 — nothing to arrange. */
  columnField: string | null
  /** Same for rows, or `null` for a board with no swimlane. */
  rowField: string | null
}

/**
 * Maps a payload into everything the host needs: the board `data` and the axis field
 * identifiers that drive grouping. Pure — no React, no fetch, no domain rules.
 *
 * The `data` mapping is delegated to the payload module's `toBoardData`, so the D16
 * discipline — four stated axis booleans, per-cell `totalCount`/`hasMoreItems` carried
 * rather than inferred from `items.length` — lives in exactly one place.
 */
export function toBoardData(payload: BoardInitialPayload): BoardSource {
  return {
    data: toBoardDataShape(payload),
    columnField: payload.columnField,
    rowField: payload.rowField,
  }
}
